import { ShopCommerce } from './commerce/ShopCommerce';
import { ProductContext } from './elements/render/productContext';
import React, { Suspense, lazy, startTransition, useEffect, useMemo, useState } from 'react';
import { storeLanguage, themeOptions } from '@xshop/shared';
import { OnePageCheckout } from './components/OnePageCheckout';
import { StorefrontNotice } from './components/StorefrontNotice';
import { OrderSuccess } from './components/OrderSuccess';
import { CodFormContext, useLoadedCodForm } from './components/useCodForm';
import { useCheckoutDisplay } from './components/useCheckoutDisplay';
import {
  bootData,
  getProductBundle,
  getActiveStore,
  StorefrontApiError,
  getTenantSettings,
  StorefrontBundle,
  StorefrontBundleTier,
  TenantSettings
} from './api/client';
import { startTracking, trackEvent, trackingAllowed } from './tracking/tracker';
import { ElementPage } from './themeHost/ElementPage';
import { cartUnits, readShellOrder, type PlacedOrder, type ShellData } from './themeHost/themeMount';
import { useStoreFacts } from './themeHost/useStoreFacts';
import { useShopCatalog } from './themeHost/useShopCatalog';
import { useProductView } from './themeHost/useProductView';
import { usePageCollection } from './themeHost/usePageCollection';
import { ListingProvider, listingPageSize } from './elements/render/ListingContext';
import { useContentPage } from './useContentPage';
import { sellsOnItsOwnPage, shownStoreName, storeBrandOf } from './themes/binding';
import { isThemePreview, markPreviewNoIndex } from './api/previewVisit';
import { useActiveTheme } from './useActiveTheme';
import { ThankYouGuard } from './commerce/ThankYouGuard';
import { withEditorDraft } from './editor/editorDraftConfig';
import { useCustomCss } from './themeHost/useCustomCss';
import { useStoreIcon } from './themeHost/useStoreIcon';
import { usePreviewLinks } from './usePreviewLinks';
import { startWebVitals } from './tracking/webVitals';
import { captureScreen, isCaptureVisit } from './themeHost/captureScreen';
import { MerchantBar } from './components/MerchantBar';
import { isEditorAddress } from './editor/editorVisit';
import { ElementSelectionProvider } from './editor/elementSelection';
import { useEditorSession } from './editor/useEditorSession';
import { StorePoliciesContext, writtenPolicies } from './components/CheckoutPolicyLinks';
import { useDrawnScreenDone } from './themeHost/firstDrawing';

// The builder's overlay — selection, spacing, drag — is fetched only when the page is drawn inside the builder,
// never by a shopper.
const EditorSelection = lazy(() => import('./editor/EditorSelection').then((m) => ({ default: m.EditorSelection })));

// Reads a catalog price as a number, refusing to invent one when the variant is missing or unparseable.
const catalogPrice = (raw: string | undefined): number => {
  const parsed = Number.parseFloat(raw ?? '');
  return Number.isFinite(parsed) ? parsed : 0;
};

// Reports whether the checkout form is drawn on this page right now, rather than waiting inside a closed dialog.
const checkoutIsOnThePage = (): boolean => {
  const card = document.getElementById('checkout-section');
  return Boolean(card && card.offsetParent !== null);
};

// The rung named on the address bar, for a shopper who chose it on the page before this one.
const askedTier = (offer: StorefrontBundle): StorefrontBundleTier | undefined => {
  const id = new URLSearchParams(window.location.search).get('tier');
  return id ? offer.tiers.find((tier) => tier.id === id) : undefined;
};

// Sends the shopper to the page this shop serves its form on, carrying the rung they chose so that page
// prices the same line this one showed them; the store and product they arrived with travel along unchanged.
const goToCheckoutPage = (tierId: string | null): void => {
  const url = new URL(window.location.href);
  url.searchParams.set('page', 'checkout');
  if (tierId) url.searchParams.set('tier', tierId);
  window.location.assign(url.toString());
};

// Root storefront landing page orchestrator
export const App: React.FC = () => {
  // The shop's products and the one this page sells, read once; a grid and the page's bindings read them.
  const { product, products, addressed, productMissing } = useShopCatalog();
  const [bundle, setBundle] = useState<StorefrontBundle | null>(null);
  const [selectedTier, setSelectedTier] = useState<StorefrontBundleTier | null>(null);
  const [completedOrder, setCompletedOrder] = useState<PlacedOrder | null>(null);
  // The store's settings the page carries, so the first drawing — on the server too — has its name and currency.
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(() => bootData<TenantSettings>('/tenant/settings') ?? null);
  // A merchant's domain that no store has connected, as the API answered.
  const [unknownDomain, setUnknownDomain] = useState(false);
  // The merchant's COD form, read once: it carries both the fields to render and where to render them.
  const codForm = useLoadedCodForm();
  // Which theme this visit renders and which page of it, plus the config the server resolved for that page:
  // ?theme= previews one, otherwise the store's own installed theme, narrowed to the pages this shop serves.
  const activeTheme = useActiveTheme(codForm?.checkoutDisplay ?? null, productMissing);
  // A preview's links are plain shop routes, so they are completed with the query that makes this a preview.
  usePreviewLinks();
  // The builder draws this page inside its own frame: what it holds is an unsaved draft of the merchant's
  // edits, which stands in for the saved ones until they press Save.
  const editor = useEditorSession(activeTheme.config);
  // Inside the builder the palette, page and stylesheet it is holding win over the saved ones, so an edit
  // shows before anything is saved; outside it they are the page exactly as the server sent it.
  const { config: themeConfig, tree, customCss } = withEditorDraft(activeTheme.config, editor);
  useCustomCss(customCss);

  // Fetch tenant info and products from backend on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isEmbedded = window.self !== window.top || new URLSearchParams(window.location.search).get('toolbar') === 'false';
      if (isEmbedded) {
        document.documentElement.classList.add('is-embedded');
        document.body.setAttribute('data-embedded', 'true');
      }
      // Embedding suppresses scrolling, which is right for the still thumbnails on the themes screen
      // and wrong for the builder, whose preview is a page the merchant reads their way down.
      if (isEditorAddress()) document.documentElement.classList.add('is-editor');
    }

    // 0. Open the visit's session and record the landing page view; a preview is nobody's shopper,
    // so it neither counts as traffic nor fires the merchant's pixels, and never reaches an index.
    // Neither a preview nor the builder is a shopper: neither counts as traffic, fires the merchant's
    // pixels, or belongs in a search result.
    // A capture is the admin taking a picture of the store, which is no visit either; it draws itself once it settles.
    // A shopper's visit is also timed, as Core Web Vitals are, for the Online Store page's speed strip.
    if (isThemePreview() || isEditorAddress() || isCaptureVisit()) markPreviewNoIndex();
    else {
      startTracking();
      if (trackingAllowed()) startWebVitals();
    }
    if (isCaptureVisit()) void captureScreen();

    // 1. Fetch store settings (name, currency)
    getTenantSettings()
      .then((settings) => {
        if (settings) setTenantSettings(settings);
      })
      .catch((err) => {
        // A domain no store has connected is answered 404 by the API: there is no shop to draw here.
        if (getActiveStore()?.custom && err instanceof StorefrontApiError && err.status === 404) setUnknownDomain(true);
        console.warn('Tenant settings unavailable:', err.message);
      });
  }, []);

  // 3. Load the merchant's active offer, priced by the server against the variant that will be ordered
  useEffect(() => {
    const variantId = product.variants[0]?.id;
    if (!variantId) return;
    let cancelled = false;
    getProductBundle(product.id, variantId)
      .then((offer) => {
        if (cancelled) return;
        // A rung carried over from the page before wins, else the merchant's default. A transition: the offer lands after the page is up.
        startTransition(() => { setBundle(offer); setSelectedTier(offer ? askedTier(offer) || offer.tiers.find((tier) => tier.isDefault) || offer.tiers[0] : null); });
      })
      .catch((err) => {
        if (!cancelled) console.warn('Bundle offer unavailable:', err.message);
      });
    return () => { cancelled = true; };
  }, [product.id, product.variants[0]?.id]);

  const activeVariant = product.variants[0];
  const unitPrice = catalogPrice(activeVariant?.price);
  const compareAtPrice = activeVariant?.compareAtPrice ? catalogPrice(activeVariant.compareAtPrice) : null;
  const quantity = selectedTier?.quantity ?? 1;
  // The server priced this ladder; multiplying here again is how the page and the order came to disagree
  const merchandiseTotal = selectedTier ? selectedTier.pricing.lineTotal : unitPrice * quantity;
  const currency = tenantSettings?.currency || 'MAD';
  useProductView(addressed ? product : null, tenantSettings ? currency : null);
  const storeName = shownStoreName(tenantSettings?.settings, tenantSettings?.name || '', activeTheme.config?.name);
  const images = product.images ?? [];
  const { display, isOpen, open, close } = useCheckoutDisplay(codForm);
  // Already narrowed to the pages this shop serves, and already the page the server resolved its config for.
  const page = activeTheme.page;
  // The page the merchant wrote, when this visit is for one; which theme dresses it is decided below.
  const contentPage = useContentPage(page, storeLanguage(tenantSettings?.settings));

  // Reaching the checkout: the page that holds the form when the shop gave it one, the form itself when it
  // is already drawn here, and the dialog when it is not. Reading the setting instead of the page is how a
  // COD lander that embeds its form under any setting ended up with a buy button that pressed nothing.
  const openCheckout = () => {
    if (checkoutIsOnThePage()) return document.getElementById('checkout-section')?.scrollIntoView({ behavior: 'smooth' });
    if (sellsOnItsOwnPage(display)) return goToCheckoutPage(selectedTier?.id ?? null);
    return open();
  };

  // A placed order closes the checkout it was placed from, so the receipt is never stacked over a stale form; the form reports the purchase.
  const handleOrderPlaced = (orderData: PlacedOrder) => {
    setCompletedOrder(orderData);
    close();
  };

  // Choosing a bundle rung is this storefront's add-to-cart signal, whichever surface it was chosen on.
  const selectTier = (tier: StorefrontBundleTier) => {
    setSelectedTier(tier);
    trackEvent('add_to_cart', { productId: product.id });
  };

  // The one checkout form this storefront has. The theme's declared direction is its one language
  // signal, and the form's fallback wording follows it.
  const formDirection = themeConfig?.direction ?? 'ltr';
  const language = storeLanguage(tenantSettings?.settings);
  // The policies the store wrote, linked under every order form; read once per visit with the settings.
  const policies = useMemo(() => writtenPolicies(tenantSettings?.settings), [tenantSettings]);
  // Once the page is taken over, media queries answer for the real window instead of the screen the server drew for.
  useDrawnScreenDone();
  const checkoutForm = (
    <OnePageCheckout
      config={codForm}
      productId={product.id}
      variantId={activeVariant?.id ?? ''}
      lineTotal={merchandiseTotal}
      quantity={quantity}
      currency={currency}
      direction={formDirection}
      language={language}
      onSuccess={handleOrderPlaced}
    />
  );

  // What a bound prop reads: the same facts the shell already holds, held steady so the tree is not
  // redrawn on every render of this component.
  const pageCollection = usePageCollection(page, language);
  const brand = storeBrandOf(tenantSettings as { settings?: Record<string, unknown> | null } | null, storeName);
  useStoreIcon(brand.markUrl);
  const storeFacts = useStoreFacts({
    storeName, logoUrl: brand.logoUrl,
    currency, direction: formDirection, page, product, images, settings: tenantSettings?.settings,
    unitPrice, compareAtPrice, quantity, merchandiseTotal, collection: pageCollection, content: contentPage
  });

  // The whole shop in one shape, so a theme renders what the shell resolved instead of fetching it again.
  const shell: ShellData = {
    storeName, currency, product, products, images, unitPrice, compareAtPrice, quantity,
    moneyLocale: formDirection === 'rtl' ? 'ar-MA' : 'fr-MA',
    cartCount: cartUnits(product, quantity, page),
    merchandiseTotal, bundle, selectedTier, onSelectTier: selectTier, checkoutForm,
    display, isOpen, openCheckout, onOpen: open, onClose: close, order: readShellOrder(completedOrder)
  };

  // Nothing below can be drawn for a visit that names no store, and guessing one is what this replaced.
  // The one thing this visitor can act on is opening a shop of their own, so that is what it offers.
  if (!getActiveStore() || unknownDomain) {
    return (
      <StorefrontNotice
        title="This shop is unavailable"
        detail="The address may be wrong, or the shop may have closed. If you are here to sell, opening one of your own takes a couple of minutes."
      />
    );
  }

  // A store with no plan running is closed to shoppers, as Shopify's stores sit behind a password page until a plan
  // is paid: the shop says it is opening soon and offers nothing to buy. The builder still draws it for its merchant.
  if (tenantSettings?.open === false && !editor.active) {
    return <StorefrontNotice title="Opening soon" detail={`${storeName || 'This shop'} is getting ready. Come back soon to see what is in store.`} />;
  }

  // Until the read that says which theme lands, there is nothing to paint.
  //
  // This holds a quiet ground rather than a notice. A notice is the answer to "there is nothing here",
  // and a shop that is merely still loading is not that — showing one flashed a dead end at shoppers on
  // their way into a working store, for as long as the theme read took.
  if (!activeTheme.resolved) {
    return <div className="sf-loading" aria-busy="true" />;
  }

  // The read landed and named no theme: this store has installed none, or the platform ships none yet.
  // It used to hold the loading ground here too, on the assumption that a store always has a theme —
  // so a shop in exactly this state spun for ever and never said why.
  if (!activeTheme.slug) {
    return (
      <StorefrontNotice
        title="This shop has no theme yet"
        detail="Its owner needs to install one before there is anything to show here."
      />
    );
  }

  // A page with nothing on it is a shop mid-setup rather than a missing one, and saying so beats a
  // blank white page. Inside the builder an empty page is the correct thing to draw: it is what the
  // merchant is about to drop their first element onto.
  if (!editor.active && (!tree || tree.roots.length === 0)) {
    return (
      <StorefrontNotice
        title="This shop is being set up"
        detail="Its pages are still being built. Please come back shortly."
      />
    );
  }

  // The tree owns the whole page; the receipt stays outside it so a placed order is never lost behind one.
  //
  // The selection is published around all of it, so the tree drawn below can open the tab or the slide
  // that holds what the builder has selected. Outside the builder it publishes null.
  return (
    <CodFormContext.Provider value={codForm}><StorePoliciesContext.Provider value={policies}><ShopCommerce currency={currency} direction={formDirection} language={language} cartAs={themeOptions(themeConfig?.styles).cartType} checkoutOnPage={themeOptions(themeConfig?.styles).checkoutPage} predictive={themeOptions(themeConfig?.styles).predictiveSearch}><ProductContext.Provider value={product}><ElementSelectionProvider id={editor.selectedElement}>
      <ThankYouGuard page={page} />
      <MerchantBar page={page} />
      {tree && themeConfig ? (
        <ListingProvider page={page} pageSize={listingPageSize(tree.roots)}>
          <ElementPage tree={tree} config={themeConfig} store={storeFacts} motionPreview={editor.active && editor.view.preview} />
        </ListingProvider>
      ) : null}
      {editor.active && <Suspense fallback={null}><EditorSelection session={editor} /></Suspense>}
      {shell.order ? (
        <OrderSuccess order={shell.order} currency={currency} onClose={() => setCompletedOrder(null)} />
      ) : null}
    </ElementSelectionProvider></ProductContext.Provider></ShopCommerce></StorePoliciesContext.Provider></CodFormContext.Provider>
  );
};

export default App;
